import { encodeFunctionData, keccak256, parseUnits, toHex, type Address, type Hex } from 'viem';
import { bondDepositoryAbi, buybackExecutorAbi, syntraTimelockAbi, referralManagerAbi, stakingManagerAbi } from '@syntra/abi';

export const PAUSER_ROLE = keccak256(toHex('PAUSER_ROLE'));
export const PROPOSER_ROLE = keccak256(toHex('PROPOSER_ROLE'));
export const EXECUTOR_ROLE = keccak256(toHex('EXECUTOR_ROLE'));
export const CANCELLER_ROLE = keccak256(toHex('CANCELLER_ROLE'));
export const ZERO_BYTES32 = `0x${'0'.repeat(64)}` as Hex;

export interface MarketConfig {
  asset: Address;
  capacityPerPeriod: bigint;
  targetDebt: bigint;
  maxPayout: bigint;
  periodLength: bigint;
  decayWindow: bigint;
  roiMinBps: number;
  roiMaxBps: number;
  enabled: boolean;
}

export type Change =
  | { kind: 'rates'; aprPct: string[] } // the six plans' APR, % (compounded each epoch: rate = APR / 730)
  | {
      kind: 'fees';
      noLockInPct: string;
      noLockOutPct: string;
      lockedInPct: string; // the four timed locks
      lockedOutPct: string;
      permanentInPct: string; // the permanent plan has its own (the client's choice: none)
      permanentOutPct: string;
      recipient: Address;
    }
  | { kind: 'market'; marketId: number; current: MarketConfig; enabled: boolean; roiMinPct: string; roiMaxPct: string; capacity: string }
  | { kind: 'buyback'; triggerPct: string; maxPerTrade: string; maxPerDay: string; slippagePct: string; cooldownHours: string; burn: boolean }
  | { kind: 'unpause'; module: 'staking' | 'bonds' | 'referral' }
  | { kind: 'upgrade'; proxy: 'staking' | 'bonds' | 'referral'; implementation: Address }; // UUPS: the proxy keeps its storage

/**
 * Making a wallet an admin, or no longer one: the timelock's proposer, executor and canceller roles, and pausing.
 * A veto wallet holds the canceller role alone: it can stop a scheduled change and nothing else (audit H-2).
 */
export type RoleChange = { kind: 'addAdmin' | 'removeAdmin' | 'addVeto' | 'removeVeto'; account: Address };

const isRoleChange = (c: Change | RoleChange): c is RoleChange => c.kind === 'addAdmin' || c.kind === 'removeAdmin' || c.kind === 'addVeto' || c.kind === 'removeVeto';

/** The ERC-1967 implementation slot: what every UUPS implementation's `proxiableUUID()` must return. */
export const UUPS_UUID = '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc' as Hex;

export interface Call {
  target: Address;
  data: Hex;
}

/** "0.3" (% per epoch) → 0.003e18; "25" (%) → 2500 bps. */
export const pctToRate = (s: string) => parseUnits(s || '0', 16);
/** "50" (% APR, compounded every 12 h epoch) → the per-epoch rate the contract takes: APR / 730. */
export const aprPctToRate = (s: string) => parseUnits(s || '0', 16) / 730n;
export const pctToBps = (s: string) => Number(parseUnits(s || '0', 2));

/** The call the timelock will make, built from a form. */
export function buildChange(c: Change, d: { staking: Address; bonds: Address; buyback: Address; referral: Address }): { target: Address; data: Hex } {
  switch (c.kind) {
    case 'rates': {
      const r = c.aprPct.map(aprPctToRate);
      return {
        target: d.staking,
        data: encodeFunctionData({ abi: stakingManagerAbi, functionName: 'setRates', args: [[r[0], r[1], r[2], r[3], r[4], r[5]]] }),
      };
    }
    case 'fees': {
      const fin = BigInt(pctToBps(c.noLockInPct));
      const fout = BigInt(pctToBps(c.noLockOutPct));
      const lin = BigInt(pctToBps(c.lockedInPct));
      const lout = BigInt(pctToBps(c.lockedOutPct));
      const pin = BigInt(pctToBps(c.permanentInPct));
      const pout = BigInt(pctToBps(c.permanentOutPct));
      return {
        target: d.staking,
        data: encodeFunctionData({
          abi: stakingManagerAbi,
          functionName: 'setFees',
          // [no lock, 30 / 90 / 180 / 365-day locks, permanent]
          args: [[fin, lin, lin, lin, lin, pin], [fout, lout, lout, lout, lout, pout], c.recipient],
        }),
      };
    }
    case 'market':
      return {
        target: d.bonds,
        data: encodeFunctionData({
          abi: bondDepositoryAbi,
          functionName: 'updateMarket',
          args: [
            BigInt(c.marketId),
            {
              ...c.current,
              enabled: c.enabled,
              roiMinBps: pctToBps(c.roiMinPct),
              roiMaxBps: pctToBps(c.roiMaxPct),
              capacityPerPeriod: parseUnits(c.capacity || '0', 18),
              maxPayout: c.current.maxPayout > parseUnits(c.capacity || '0', 18) ? parseUnits(c.capacity || '0', 18) : c.current.maxPayout,
            },
          ],
        }),
      };
    case 'buyback':
      return {
        target: d.buyback,
        data: encodeFunctionData({
          abi: buybackExecutorAbi,
          functionName: 'setConfig',
          args: [
            {
              triggerBps: pctToBps(c.triggerPct),
              slippageBps: pctToBps(c.slippagePct),
              cooldown: Math.round(Number(c.cooldownHours) * 3600),
              maxPerTrade: parseUnits(c.maxPerTrade || '0', 18),
              maxPerDay: parseUnits(c.maxPerDay || '0', 18),
              burnBought: c.burn,
            },
          ],
        }),
      };
    case 'unpause': {
      const target = c.module === 'staking' ? d.staking : c.module === 'bonds' ? d.bonds : d.referral;
      const abi = c.module === 'staking' ? stakingManagerAbi : c.module === 'bonds' ? bondDepositoryAbi : referralManagerAbi;
      return { target, data: encodeFunctionData({ abi: abi as typeof stakingManagerAbi, functionName: 'unpause' }) };
    }
    case 'upgrade': {
      const target = c.proxy === 'staking' ? d.staking : c.proxy === 'bonds' ? d.bonds : d.referral;
      return { target, data: encodeFunctionData({ abi: stakingManagerAbi, functionName: 'upgradeToAndCall', args: [c.implementation, '0x'] }) };
    }
  }
}

/**
 * The calls one change makes. An admin wallet is any wallet (MetaMask or another, or a Safe) holding the timelock's
 * proposer, executor and canceller roles plus each module's pauser role, so adding or removing one is six role calls,
 * scheduled together and run together. The timelock is the admin of all of these roles.
 */
export function buildCalls(c: Change | RoleChange, d: { staking: Address; bonds: Address; buyback: Address; referral: Address; timelock: Address }): Call[] {
  if (!isRoleChange(c)) return [buildChange(c, d)];
  const functionName = c.kind === 'addAdmin' || c.kind === 'addVeto' ? 'grantRole' : 'revokeRole';
  if (c.kind === 'addVeto' || c.kind === 'removeVeto') {
    return [{ target: d.timelock, data: encodeFunctionData({ abi: syntraTimelockAbi, functionName, args: [CANCELLER_ROLE, c.account] }) }];
  }
  return [
    ...[PROPOSER_ROLE, EXECUTOR_ROLE, CANCELLER_ROLE].map((role) => ({
      target: d.timelock,
      data: encodeFunctionData({ abi: syntraTimelockAbi, functionName, args: [role, c.account] }),
    })),
    ...[d.staking, d.bonds, d.referral].map((target) => ({
      target,
      data: encodeFunctionData({ abi: stakingManagerAbi, functionName, args: [PAUSER_ROLE, c.account] }),
    })),
  ];
}

/** The timelock call that schedules these calls: `schedule` for one, `scheduleBatch` for several. */
export function scheduleRequest(calls: Call[], delay: bigint, salt: Hex) {
  return calls.length === 1
    ? ({ functionName: 'schedule', args: scheduleArgs(calls[0].target, calls[0].data, delay, salt) } as const)
    : ({ functionName: 'scheduleBatch', args: [calls.map((x) => x.target), calls.map(() => 0n), calls.map((x) => x.data), ZERO_BYTES32, salt, delay] } as const);
}

export function scheduleCallsCalldata(calls: Call[], delay: bigint, salt: Hex): Hex {
  return encodeFunctionData({ abi: syntraTimelockAbi, ...scheduleRequest(calls, delay, salt) } as Parameters<typeof encodeFunctionData>[0]);
}

/** Arguments for TimelockController.schedule; a fresh salt keeps identical changes distinct. */
export function scheduleArgs(target: Address, data: Hex, delay: bigint, salt: Hex) {
  return [target, 0n, data, ZERO_BYTES32, salt, delay] as const;
}

export function scheduleCalldata(target: Address, data: Hex, delay: bigint, salt: Hex): Hex {
  return encodeFunctionData({ abi: syntraTimelockAbi, functionName: 'schedule', args: scheduleArgs(target, data, delay, salt) });
}

/** A batch file for the Safe{Wallet} Transaction Builder app: one call to the timelock's `schedule`. */
export function safeBuilderJson(chainId: number, timelock: Address, calldata: Hex, description: string): string {
  return JSON.stringify(
    {
      version: '1.0',
      chainId: String(chainId),
      createdAt: Date.now(),
      meta: { name: 'Syntra timelock change', description },
      transactions: [{ to: timelock, value: '0', data: calldata }],
    },
    null,
    2,
  );
}
